import { z } from 'zod';

import { nonNegativeIntSchema, positiveIntSchema } from '../common/primitives';

/**
 * Wie oft gesichert und wie lange aufbewahrt wird.
 *
 * Die Zahlen stehen hier und nicht in der Konfiguration einer der beiden
 * Anwendungen, weil beide Seiten sie brauchen: Der Master plant danach, der
 * Agent gibt sie an `restic forget` weiter. Zwei Quellen für dieselbe Zahl
 * hießen, dass eine Flotte im Zweifel unterschiedlich lange aufbewahrt, ohne
 * dass es jemandem auffällt.
 */

/**
 * Gestaffelte Aufbewahrung: 7 täglich, 4 wöchentlich, 6 monatlich.
 *
 * Die Staffelung ist der Grund, warum `restic forget` und nicht ein simples
 * "lösche alles über 30 Tage" benutzt wird: Der häufigste Schadensfall ist ein
 * Fehler von gestern, der zweithäufigste einer, den erst die Abrechnung im
 * nächsten Monat auffallen lässt. Eine gleichmäßige Frist bedient nur den ersten.
 */
export const BACKUP_RETENTION = {
  daily: 7,
  weekly: 4,
  monthly: 6,
} as const;

export const backupRetentionSchema = z.object({
  daily: positiveIntSchema.max(365),
  weekly: nonNegativeIntSchema.max(260),
  monthly: nonNegativeIntSchema.max(120),
});

export type BackupRetention = z.infer<typeof backupRetentionSchema>;

/**
 * Obergrenze der Momentaufnahmen, die eine Aufbewahrung übrig lässt.
 *
 * Die Summe ist eine obere Schranke und keine Vorhersage: Wer sieben Tage am
 * Stück sichert, hat noch keine vier Wochen. Gebraucht wird die Zahl dort, wo
 * eine Liste begrenzt werden muss — im Vertrag der Rückmeldung und in der
 * Spalte, in der der Master die Liste ablegt.
 */
export function maxRetainedSnapshots(retention: BackupRetention): number {
  return retention.daily + retention.weekly + retention.monthly;
}

/** Abstand zweier planmäßiger Sicherungen derselben Site. */
export const BACKUP_INTERVAL_HOURS = 24;

/**
 * Das nächtliche Fenster (UTC), in dem die Flotte sichert.
 *
 * Vier Stunden und nicht ein Zeitpunkt: Alle Sites gleichzeitig zu sichern
 * hieße, dass jeder Worker gleichzeitig liest und alle gleichzeitig auf
 * dieselbe Box schreiben. Der Platz einer Site im Fenster wird aus ihrer ID
 * abgeleitet und ist damit über die Tage hinweg stabil — eine Sicherung, die
 * jede Nacht zu einer anderen Zeit läuft, lässt sich nicht vermissen.
 */
export const BACKUP_WINDOW_UTC = {
  startHour: 1,
  endHour: 5,
} as const;
